import { TAGSvgIcon } from '@aspendental/shared-components-web';
import TAGButton from '@aspendental/shared-components-web/lib/components/TAGButton';
import { Box, Typography } from '@mui/material';
import { addDays, format, parse } from 'date-fns';
import { useEffect, useState } from 'react';
import { getTimeSlotsStepBottomTextWrapper, getTimeSlotsStepDayTitle, getTimeSlotsStepShowMoreTimesButton, timeSlotsStepPreviousAndNextButtonsWrapper, timeSlotsStepWrapperStyles } from './TimeSlotsStep.styles';
import { TimeSlotsStepLoading } from './TimeSlotsStepLoading/TimeSlotsStepLoading';
import { TimeSlotsSwiper } from './TimeSlotsSwiper/TimeSlotsSwiper';
import { treatmentCodeToReasonCode } from '@src/constants';
import { ISO8601DateFormat, useGetTimeSlots } from '@src/services/useGetTimeSlots/useGetTimeSlots';
import { useSchedulingStore } from '@src/store';
import { daysBetweenDates } from '@src/utils/daysBetweenDates';
import { pushToDataLayer } from '@src/utils/pushToDataLayer';
type TimeSlotsStepProps = {
  index: number;
};
const today = parse(format(new Date(), ISO8601DateFormat), ISO8601DateFormat, new Date());
today.setHours(0, 0, 0, 0);
export const TimeSlotsStep = ({
  index
}: TimeSlotsStepProps) => {
  const {
    currentStepIndex,
    changeCurrentStepIndex,
    selectedTimeslot,
    setSelectedTimeslot,
    selectedLocation,
    selectedTreatmentId,
    selectedTreatmentName,
    selectedTreatmentCode,
    setIsTimeslotsLoading
  } = useSchedulingStore(state => state);
  const [startingDate, setStartingDate] = useState(today);
  const [isShowMoreTimesButtonClicked, setIsShowMoreTimesButtonClicked] = useState(false);
  const [timeslotPagesFirstDates, setTimeslotPagesFirstDates] = useState<Date[]>([]);
  const {
    data: datesAndTimeslots,
    isFetching,
    error
  } = useGetTimeSlots(selectedLocation?.id, selectedTreatmentId, startingDate);
  const isStepDisabled = currentStepIndex < index;
  const isLessThanThreeTimeslots = datesAndTimeslots ? datesAndTimeslots.length < 3 : true;
  const isPreviousButtonDisabled = isStepDisabled || timeslotPagesFirstDates.length === 0;
  const isNextButtonDisabled = isStepDisabled || isLessThanThreeTimeslots;
  useEffect(() => {
    setIsTimeslotsLoading(isFetching);
  }, [isFetching, setIsTimeslotsLoading]);

  // if the selectedLocation or selectedTreatmentId change, reset the startingDate, timeslotPagesFirstDates, selectedTimeslot and return back to this step
  useEffect(() => {
    if (!isStepDisabled) {
      setStartingDate(today);
      setTimeslotPagesFirstDates([]);
      setSelectedTimeslot(null);
      changeCurrentStepIndex(index);
    }
  }, [changeCurrentStepIndex, index, isStepDisabled, selectedLocation, selectedTreatmentId, setSelectedTimeslot]);
  const addNewStartingDateToTimeslotPagesFirstDate = () => {
    for (const firstDateOnPage of timeslotPagesFirstDates) {
      if (firstDateOnPage.toDateString() === startingDate.toDateString()) {
        return;
      }
    }
    setTimeslotPagesFirstDates(prev => [...prev, startingDate]);
  };
  const onShowMoreTimesButtonClick = () => {
    addNewStartingDateToTimeslotPagesFirstDate();
    setIsShowMoreTimesButtonClicked(true);
    const dataForDataLayer = {
      event: 'appointment_step',
      stepName: 'show_more_times',
      startingDate,
      selectedTimeslot: selectedTimeslot,
      daysToAppointment: selectedTimeslot ? daysBetweenDates(selectedTimeslot, new Date()) : null
    };
    pushToDataLayer(dataForDataLayer);
    const lastDateOnPage = datesAndTimeslots?.[datesAndTimeslots?.length - 1].date;
    if (lastDateOnPage) {
      setStartingDate(addDays(parse(lastDateOnPage, ISO8601DateFormat, new Date()), 1));
    }
  };
  const onPreviousButtonClick = () => {
    if (isPreviousButtonDisabled) {
      return;
    }
    const timeslotPagesFirstDateTemp = [...timeslotPagesFirstDates];
    const newStartingDate = timeslotPagesFirstDateTemp.pop();
    setTimeslotPagesFirstDates(timeslotPagesFirstDateTemp);
    if (newStartingDate) {
      setStartingDate(newStartingDate);
    }
  };
  const onNextButtonClick = () => {
    addNewStartingDateToTimeslotPagesFirstDate();
    const lastDateOnPage = datesAndTimeslots?.[datesAndTimeslots?.length - 1]?.date;
    if (lastDateOnPage) {
      setStartingDate(addDays(parse(lastDateOnPage, ISO8601DateFormat, new Date()), 1));
    }
  };
  const segmentType = useSchedulingStore().returningGuest ? 1 : 0;
  const handleTimeslotChange = (timeslot: string) => {
    setSelectedTimeslot(timeslot);
    const dataForDataLayer = {
      event: 'appointment_step',
      stepName: 'select_date_and_time',
      startingDate,
      selectedTimeslot: timeslot,
      daysToAppointment: daysBetweenDates(new Date(timeslot), new Date()),
      treatmentId: selectedTreatmentId,
      treatmentName: selectedTreatmentName,
      reasonCode: treatmentCodeToReasonCode[selectedTreatmentCode],
      segmentType: segmentType
    };
    pushToDataLayer(dataForDataLayer);
  };
  if (error) {
    return <Typography variant="bodyMediumBook"> Oops! Please refresh the page and try again. </Typography>;
  }
  if (!datesAndTimeslots || isFetching) {
    return <TimeSlotsStepLoading />;
  }
  return <Box sx={timeSlotsStepWrapperStyles} data-test-id="time-slots-step-wrapper" data-sentry-element="Box" data-sentry-component="TimeSlotsStep" data-sentry-source-file="TimeSlotsStep.tsx">
			{datesAndTimeslots.length === 0 && <p> There are no available slots </p>}

			{datesAndTimeslots.map(({
      date,
      slots
    }) => {
      return <Box key={date}>
						<Typography component="p" variant="bodySmallBold" sx={getTimeSlotsStepDayTitle(isStepDisabled)}>
							{format(parse(date, ISO8601DateFormat, new Date()), 'EEE, MMM d')}
						</Typography>

						<TimeSlotsSwiper slots={slots} selectedTimeslot={selectedTimeslot} isStepDisabled={isStepDisabled} handleTimeslotChange={handleTimeslotChange} />
					</Box>;
    })}

			{!isLessThanThreeTimeslots && !isShowMoreTimesButtonClicked && <TAGButton data-test-id="show-more-times-button" disabled={isStepDisabled} sx={getTimeSlotsStepShowMoreTimesButton(isStepDisabled)} variant="tertiaryDefault" onClick={onShowMoreTimesButtonClick} size="S">
					<Typography variant="bodySmallSemiBold">SHOW MORE TIMES</Typography>
				</TAGButton>}

			{isShowMoreTimesButtonClicked && <Box sx={timeSlotsStepPreviousAndNextButtonsWrapper}>
					<TAGButton disabled={isPreviousButtonDisabled} variant="tertiaryDefault" onClick={onPreviousButtonClick} size="S">
						<TAGSvgIcon icon="SortUp" /> PREVIOUS
					</TAGButton>
					<TAGButton size="S" disabled={isNextButtonDisabled} variant="tertiaryDefault" onClick={onNextButtonClick}>
						<TAGSvgIcon icon="SortDown" /> NEXT
					</TAGButton>
				</Box>}

			<Box sx={getTimeSlotsStepBottomTextWrapper(isStepDisabled)} data-sentry-element="Box" data-sentry-source-file="TimeSlotsStep.tsx">
				<Typography component="p" variant="bodyMediumBook" data-sentry-element="Typography" data-sentry-source-file="TimeSlotsStep.tsx">
					{"Don't see a date and time that works for you?"}
				</Typography>
				<Typography variant="bodyMediumBook" data-sentry-element="Typography" data-sentry-source-file="TimeSlotsStep.tsx">
					{'Give us a call at '}
					<Typography variant="bodyMediumBold" component="a" href="tel:7012972015" data-sentry-element="Typography" data-sentry-source-file="TimeSlotsStep.tsx">
						701-297-2015
					</Typography>
				</Typography>
			</Box>
		</Box>;
};